/* eslint-disable max-lines */
import React, { useCallback, useEffect, useRef, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import CancelSharpIcon from '@material-ui/icons/CancelSharp';
import { observer } from 'mobx-react';

import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import Button from 'components/Button/Button';
import BroadcasterPublicChatEmpty from 'components/BroadcasterPublicChatEmpty/BroadcasterPublicChatEmpty';
import MessageComponent from 'components/ChatMessageTypes/MessageComponent';
import SelfMessage from 'components/ChatMessageTypes/SelfMessage';
import NotificationChatComponent from 'components/ChatNotificationsTypes/NotificationChatComponent';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import APIStates from 'enum/APIStates';
import controlledFeatures from 'enum/controlledFeatures';
import { SENT_CHAT_MESSAGE_STATUSES } from 'enum/helpers';
import {
  NOTIFICATION_TYPE,
  MESSAGE_FLAIRS,
  MESSAGE_TYPES,
} from 'enum/messages';
import allUserGroups from 'enum/userGroups';
import { BROADCAST_STATUS } from 'enum/videoPlayer';
import { generateID } from 'utils/helpers';
import {
  actionCreators as appActions,
  selector as appSelector,
} from 'app/AppState';
import { RegistrationModalCaller } from 'pages/RegistrationModal/utils';
import getMessagingService from 'services/MessagingService/MessagingService';
import { LOAD_INITIAL_CHAT_MESSAGES, INITIAL_MESSAGE_TRANSLATION } from 'services/MessagingService/EventNames';
import { SECOND_AS_MILLISECONDS } from 'enum/timeFormat';

import ChatInputError from '../ChatInputError/ChatInputError';
import ChatInputMobileV2 from '../ChatInputMobileV2/ChatInputMobileV2';
import ChatInteractionActions from '../ChatInteractionActions/ChatInteractionActions';

import styles from './ChatMobileMessages.css';
import { getEstimatedMessageHeight } from './utils';


// eslint-disable-next-line complexity
const ChatMobileMessages = observer((props) => {
  const {
    roomName,
    appActions: { setShowRegistrationModal },
    setIsInputFocused,
    isInputFocused,
    openDrawer,
    messages,
    rosterUsers,
    toggleGameDrawer,
    isPrivateChat,
    isGroupShowChat,
    showBuyTokensModal,
    isDesktop,
    handleTabsChange,
    isPrivateUserChat,
    recipient,
    handlePrivateChat,
    hideInputInPrivateChat,
    embedProfileV2,
    isBroadcasterChat,
    isGSTimerVisible,
  } = props;
  const {
    chatSettings: viewerChatSettings,
    chatSettingsPerformer,
    isChatInputErrorVisible,
    handleChatInputError,
    chatInputErrorMessage,
    activeConversation,
    deleteChat,
    resetActiveConversation,
    togglePrivateMessages,
    privateUserChats,
    hasUnreadMessages,
    hasPrivateUnreadMessages,
    hasUnreadUser2UserMessages,
    hasUnreadGsMessages,
    toggleUnreadMessages,
    setScrollAtBottom,
  } = ChatStore;
  const { user } = UserStore;
  const messagingService = getMessagingService();
  const chatSettings = isBroadcasterChat ? chatSettingsPerformer : viewerChatSettings;
  const { isLoggedInUserRoomModerator, isUserSilencedByRoomModerator, isStreamPortrait } = FirebaseStore;
  const messagesHolderRef = useRef(null);
  const {
    _handleReportUserResponseStatus,
    _handleSilenceUserResponseStatus,
    fullscreen,
    broadcastStatus,
    inTheatreMode,
    inCustomFullscreen,
  } = ProfileStore;
  // Not to break when user is read from the Mobx store
  const isSfw = user.sfw || user.user?.getIn(['user', 'sfw']);
  const IDS = {
    prefix: 'er034OPz',
    element: 'chatWrapper',
    showNewMsgBtn: 'showNewMsgBtn',
    showNewMsgBtnMobile: 'showNewMsgBtnMobile',
  };
  const makeId = generateID(IDS.prefix, IDS.element);
  const gifsInChat = UserStore.hasCF(allUserGroups.GIFS_IN_PLAYER_CHAT);
  const isPrivateAdminMessage =
    activeConversation &&
    [MESSAGE_TYPES.ADMIN, MESSAGE_TYPES.MODERATOR, MESSAGE_TYPES.USER_MODERATOR].includes(
      privateUserChats[activeConversation]?.accessLevel
    );

  const [showNewMsg, setShowNewMsg] = useState(false);

  const isScrollAtBottom = useCallback(() => {
    const node = messagesHolderRef.current;
    if (!node) {
      return false;
    }

    const lastMessage = messages[messages.length - 1];
    const threshold = getEstimatedMessageHeight(lastMessage);

    const result = node.offsetHeight + node.scrollTop + threshold >= node.scrollHeight;
    setScrollAtBottom(result);

    return result;
  }, [messages, setScrollAtBottom]);

  const scrollingThroughMessages = useCallback(() => {
    setShowNewMsg(hasUnreadMessages || hasPrivateUnreadMessages || hasUnreadUser2UserMessages || hasUnreadGsMessages);

    if (isScrollAtBottom()) {
      setShowNewMsg(false);
      toggleUnreadMessages(false);
    }
  }, [
    hasUnreadMessages,
    hasPrivateUnreadMessages,
    hasUnreadUser2UserMessages,
    hasUnreadGsMessages,
    isScrollAtBottom,
    toggleUnreadMessages,
  ]);

  const scrollToBottom = useCallback(() => {
    const node = messagesHolderRef.current;
    if (!node) {
      return;
    }
    node.scrollTop = node.scrollHeight;
    setScrollAtBottom(true);
  }, [setScrollAtBottom]);

  useEffect(() => {
    window.addEventListener('resize', scrollToBottom);

    return () => {
      window.removeEventListener('resize', scrollToBottom);
    };
  }, [scrollToBottom]);

  useEffect(() => {
    const asyncScrollToBottom = () => {
      setTimeout(() => {
        const node = messagesHolderRef.current;
        if (!node) {
          return;
        }
        node.scrollTop = node.scrollHeight;
        setScrollAtBottom(true);
      }, 0);
    };
    asyncScrollToBottom();
    messagingService.on(LOAD_INITIAL_CHAT_MESSAGES, asyncScrollToBottom);

    return () => {
      messagingService.off(LOAD_INITIAL_CHAT_MESSAGES, asyncScrollToBottom);
    };
  }, [messagingService, setScrollAtBottom]);

  useEffect(() => {
    const WAITING_TIME = 3000;
    const asyncScrollTranslation = () => {
      setTimeout(() => {
        const node = messagesHolderRef.current;
        if (!node) {
          return;
        }
        node.scrollTop = node.scrollHeight;
      }, WAITING_TIME);
    };
    messagingService.on(INITIAL_MESSAGE_TRANSLATION, asyncScrollTranslation);

    return () => {
      messagingService.off(INITIAL_MESSAGE_TRANSLATION, asyncScrollTranslation);
    };
  }, [messagingService]);

  const clickedOnShowNewMsg = useCallback(() => {
    scrollToBottom();
    setShowNewMsg(false);
    toggleUnreadMessages(false);
  }, [scrollToBottom, toggleUnreadMessages]);

  useEffect(() => {
    if (isScrollAtBottom() || fullscreen || inCustomFullscreen || inTheatreMode) {
      scrollToBottom();
      setShowNewMsg(false);
      toggleUnreadMessages(false);
    } else if (hasUnreadMessages || hasPrivateUnreadMessages || hasUnreadUser2UserMessages || hasUnreadGsMessages) {
      setShowNewMsg(true);
      toggleUnreadMessages(true);
    }
  }, [
    messages.length,
    fullscreen,
    inCustomFullscreen,
    inTheatreMode,
    hasUnreadMessages,
    hasPrivateUnreadMessages,
    hasUnreadUser2UserMessages,
    hasUnreadGsMessages,
    isScrollAtBottom,
    scrollToBottom,
    toggleUnreadMessages,
  ]);

  useEffect(() => {
    const ERROR_TIMEOUT_SMALL = 3 * SECOND_AS_MILLISECONDS;
    const ERROR_TIMEOUT_BIG = 10 * SECOND_AS_MILLISECONDS;
    const SMALL_MESSAGE_STATUSES = [
      SENT_CHAT_MESSAGE_STATUSES.THROTTLER_MESSAGE_SPAM,
      SENT_CHAT_MESSAGE_STATUSES.BROADCASTERS_NOT_ALLOWED_TO_CHAT,
      SENT_CHAT_MESSAGE_STATUSES.SPAM_DETECTED,
      SENT_CHAT_MESSAGE_STATUSES.EMPTY_MESSAGE,
      SENT_CHAT_MESSAGE_STATUSES.GIPHY_SENT_TOO_SOON,
    ];
    const timer =
      isChatInputErrorVisible &&
      setTimeout(
        () => {
          handleChatInputError(false);
        },
        SMALL_MESSAGE_STATUSES.includes(chatInputErrorMessage) ? ERROR_TIMEOUT_SMALL : ERROR_TIMEOUT_BIG
      );

    return () => isChatInputErrorVisible && clearTimeout(timer);
  }, [handleChatInputError, chatInputErrorMessage, isChatInputErrorVisible]);

  const openGuestBillingModal = useCallback(() => {
    ModalService.showModal(ModalService.MODAL_KEYS.GUEST_BILLING_MODAL, {
      close: () => ModalService.hideModal(),
      show: true,
    });
  }, []);

  const canProceedWithAction = useCallback(() => {
    const isGuestBilling = user.accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
    const isGuest = user.accessLevel === ACCESS_LEVEL_NUM.GUEST;
    if (isGuest && !UserStore.hasCF(controlledFeatures.GUEST_CHAT_ACCESS)) {
      setShowRegistrationModal(true, RegistrationModalCaller.CHAT_MOBILE_MESSAGES);

      return false;
    }

    if (isGuestBilling) {
      openGuestBillingModal();

      return false;
    }

    return true;
  }, [user.accessLevel, setShowRegistrationModal, openGuestBillingModal]);

  const openContextualChatModal = useCallback((targetedUsername, isPerformer = false) => {
    const modalProps = {
      targetedUsername,
      user,
      isLoggedInUserRoomModerator: isLoggedInUserRoomModerator(user.username),
      roomName,
      isUserSilencedByRoomModerator: isUserSilencedByRoomModerator(targetedUsername),
      isPerformer,
      handlePrivateChat,
    };
    if (canProceedWithAction()) {
      _handleReportUserResponseStatus(APIStates.INITIAL);
      _handleSilenceUserResponseStatus(APIStates.INITIAL);
      ModalService.showModal(ModalService.MODAL_KEYS.CHAT_CONTEXTUAL_OVERLAY, modalProps);
    }
  }, [
    isLoggedInUserRoomModerator,
    roomName,
    user,
    canProceedWithAction,
    handlePrivateChat,
    isUserSilencedByRoomModerator,
    _handleReportUserResponseStatus,
    _handleSilenceUserResponseStatus,
  ]);

  const chooseErrorTranslatedMsg = () => {
    switch (chatInputErrorMessage) {
      case SENT_CHAT_MESSAGE_STATUSES.THROTTLER_MESSAGE_SPAM:
        return translate('v2profile.spamming.chat.same.message');
      case SENT_CHAT_MESSAGE_STATUSES.SPAM_DETECTED:
        return translate('v2profile.spam.detected');
      case SENT_CHAT_MESSAGE_STATUSES.EMPTY_MESSAGE:
        return translate('v2profile.cant.send.empty.message');
      case SENT_CHAT_MESSAGE_STATUSES.BROADCASTERS_NOT_ALLOWED_TO_CHAT:
        return translate('strings.ChatRoomManager.chatError.notAllowedToChat');
      case SENT_CHAT_MESSAGE_STATUSES.NOT_IN_THE_ROOM:
        return translate('v2profile.chat.disconected.tooltip');
      case SENT_CHAT_MESSAGE_STATUSES.GENDER_NOT_ALLOWED_TO_CHAT:
      case SENT_CHAT_MESSAGE_STATUSES.ONLY_FRIENDS_FAVORITES_ALLOWED_TO_CHAT:
      case SENT_CHAT_MESSAGE_STATUSES.USER_LANGUAGE_NOT_ALLOWED_TO_CHAT:
        return translate('v2broadcast.chat.limited');
      case SENT_CHAT_MESSAGE_STATUSES.GIPHY_SENT_TOO_SOON:
        return translate('v2broadcast.gif.sent.too.soon');

      default:
        return translate('v2profile.sending.too.many.messages');
    }
  };

  const deletePrivateConversation = useCallback(() => {
    resetActiveConversation();
    togglePrivateMessages(false);
    deleteChat(activeConversation);
  }, [activeConversation, deleteChat, resetActiveConversation, togglePrivateMessages]);

  const wrapperClasses = classNames({
    [styles.gamesAndChatWrapperPrivateInFullScreen]: fullscreen && isPrivateChat,
    [styles.desktopVersion]: isDesktop,
    [styles.activeConversationWrapper]: !isDesktop && activeConversation && !fullscreen && !isStreamPortrait,
    [styles.activeConversationWrapperFullscreen]: !isDesktop && activeConversation && (fullscreen || isStreamPortrait),
    [styles.mobileActiveConversationWrapper]: activeConversation && !isDesktop && isInputFocused,
    [styles.gamesAndChatWrapperPrivate]: isPrivateChat,
    [styles.gamesAndChatWrapperPrivateEmbed]: isPrivateChat && embedProfileV2,
    [styles.gamesAndChatEmbedWrapper]: !isPrivateChat && embedProfileV2,
    [styles.gamesAndChatWrapper]: !isPrivateChat,
    [styles.gamesAndChatEmbedWrapperSmaller]: !isPrivateChat && fullscreen && embedProfileV2,
    [styles.gamesAndChatWrapperSmaller]: !isPrivateChat && fullscreen,
    [styles.wrapperLandscapeEmbed]: !isStreamPortrait && !fullscreen && embedProfileV2,
    [styles.wrapperLandscape]: !isStreamPortrait && !fullscreen,
    [styles.groupShowWrapperLandscape]: isGroupShowChat || isGSTimerVisible,
    [styles.noPadding]: isBroadcasterChat,
  });

  const chatInputClasses = classNames({
    [styles.desktopVersion]: isDesktop,
    [styles.inputHolderPrivate]: isPrivateChat,
    [styles.inputHolder]: !isPrivateChat,
    [styles.groupShowChatInputHolder]: isGroupShowChat || isGSTimerVisible,
    [styles.inputHolderEmbed]: embedProfileV2 && !isDesktop,
  });

  const msgHolderClasses = classNames({
    [styles.msgHolder]: !embedProfileV2 && !isPrivateChat && isDesktop,
    [styles.msgHolderForMobile]: !isDesktop && !isPrivateChat,
    [styles.msgHolderForMobilePortrait]: !isDesktop && !isPrivateChat && (isStreamPortrait || fullscreen),
    [styles.privateMessageHolder]: isPrivateChat,
    [styles.msgHolderEmbed]: embedProfileV2,
    [styles.noPadding]: isBroadcasterChat,
  });

  const msgHolderWrapper = classNames({
    [styles.mobilePrivateChatsMsgHolder]: activeConversation && !isDesktop,
    [styles.mobilePrivateChatsMsgHolderFocused]: activeConversation && !isDesktop && isInputFocused,
    [styles.mobilePrivateChatsMsgHolderReconnecting]:
      activeConversation && !isDesktop && broadcastStatus === BROADCAST_STATUS.RECONNECTING,
    [styles.desktopPrivateChatsMsgHolder]: isDesktop || !activeConversation,
    [styles.mobileNotPrivateMsgHolder]: !activeConversation && !isDesktop,
    [styles.mobileNotPrivateMsgHolderForGS]: (isGroupShowChat || isGSTimerVisible) && !activeConversation && !isDesktop,
    [styles.mobileMsgHolderEmbed]: embedProfileV2 && !isDesktop,
  });

  const showButtons = !isPrivateChat && !isDesktop;
  const isBroadcasterAndPublicChatEmpty = isBroadcasterChat && messages.length === 0 && !isPrivateUserChat;

  return (
    <Fragment>
      <div className={wrapperClasses} id={props.id} role="presentation" >
        <div className={msgHolderWrapper}>
          {activeConversation && !isDesktop && (
            <div className={styles.privateUsername}>
              <span className={styles.privateConversationSenderName}>
                {isPrivateAdminMessage ? MESSAGE_FLAIRS.ADMIN : activeConversation}
              </span>
              {!isPrivateAdminMessage && <CancelSharpIcon onClick={deletePrivateConversation} />}
            </div>
          )}
          <div ref={messagesHolderRef} className={msgHolderClasses} onScroll={scrollingThroughMessages}>
            {messages.map((msg) => {
              const { hash, messageKey, isGif, type, senderOriginalUsername } = msg;
              if (isGif && !gifsInChat) {
                return null;
              }
              if (type === 1) {
                return senderOriginalUsername === user.username ? (
                  <SelfMessage
                    key={`${hash}-${messageKey}`}
                    chatSettings={chatSettings}
                    isBroadcasterChat={isBroadcasterChat}
                    isDesktop={isDesktop}
                    msg={msg}
                  />
                ) : (
                  <MessageComponent
                    key={`${hash}-${messageKey}`}
                    chatSettings={chatSettings}
                    embedProfileV2={embedProfileV2}
                    isBroadcasterChat={isBroadcasterChat}
                    isDesktop={isDesktop}
                    message={msg}
                    openContextualChatModal={openContextualChatModal}
                    openGuestBillingModal={openGuestBillingModal}
                    roomName={roomName}
                    senderFromRoster={rosterUsers?.[msg.senderOriginalUsername]}
                  />
                );
              }
              if (type !== 1 && type !== undefined && type !== NOTIFICATION_TYPE.ROOM_PASSWORD_CHANGED) {
                return (
                  <NotificationChatComponent
                    key={`${hash}-${messageKey}`}
                    chatSettings={chatSettings}
                    embedProfileV2={embedProfileV2}
                    handleTabsChange={handleTabsChange}
                    isBroadcasterChat={isBroadcasterChat}
                    isDesktop={isDesktop}
                    isSfw={isSfw}
                    message={msg}
                    msg={msg}
                    roomName={roomName}
                    setShowRegistrationModal={setShowRegistrationModal}
                    showBuyTokensModal={showBuyTokensModal}
                    toggleGameDrawer={toggleGameDrawer}
                  />
                );
              }

              return null;
            })}
            {isDesktop && showNewMsg && (
              <Button
                className={styles.scrollToNewMsgBtn}
                icon={<ArrowDownwardIcon className={styles.scrollDownIcon} />}
                id={makeId(IDS.showNewMsgBtn)}
                label={translate('v2profile.chat.new.messages.below')}
                size="small"
                onClick={clickedOnShowNewMsg}
              />
            )}
            {!isDesktop && showNewMsg && (
              <Button
                className={styles.scrollToNewMsgBtnMobile}
                icon={<ArrowDownwardIcon className={styles.scrollDownIcon} />}
                id={makeId(IDS.showNewMsgBtnMobile)}
                size="small"
                onClick={clickedOnShowNewMsg}
              />
            )}

            {isBroadcasterAndPublicChatEmpty && !isGroupShowChat && <BroadcasterPublicChatEmpty />}
          </div>
          {activeConversation && !isDesktop && (
            <div className={styles.privateConversationInput}>
              <ChatInputMobileV2
                canProceedWithAction={canProceedWithAction}
                isBroadcasterChat={isBroadcasterChat}
                isDesktop={isDesktop}
                isGroupShowChat={isGroupShowChat}
                isPrivateChat={isPrivateChat}
                isPrivateUserChat={isPrivateUserChat}
                recipient={recipient}
                roomName={roomName}
                setIsInputFocused={setIsInputFocused}
                user={user}
              />
            </div>
          )}
        </div>
        {embedProfileV2 && showButtons && (
          <ChatInteractionActions
            embedProfileV2={embedProfileV2}
            openDrawer={openDrawer}
            roomName={roomName}
            setShowRegistrationModal={setShowRegistrationModal}
            showBuyTokensModal={showBuyTokensModal}
            toggleGameDrawer={toggleGameDrawer}
            user={user}
          />
        )}
      </div>
      {(activeConversation === null || isDesktop) && !hideInputInPrivateChat && (
        <div className={chatInputClasses}>
          {isChatInputErrorVisible ? (
            <ChatInputError isDesktop={isDesktop} message={chooseErrorTranslatedMsg()} />
          ) : (
            <ChatInputMobileV2
              canProceedWithAction={canProceedWithAction}
              isBroadcasterChat={isBroadcasterChat}
              isDesktop={isDesktop}
              isGroupShowChat={isGroupShowChat}
              isPrivateChat={isPrivateChat}
              isPrivateUserChat={isPrivateUserChat}
              recipient={recipient}
              roomName={roomName}
              setIsInputFocused={setIsInputFocused}
              user={user}
            />
          )}
        </div>
      )}
    </Fragment>
  );
});

ChatMobileMessages.propTypes = {
  appActions: PropTypes.object.isRequired,
  showBuyTokensModal: PropTypes.func.isRequired,
  hideInputInPrivateChat: PropTypes.bool,
  isBroadcasterChat: PropTypes.bool,
  isDesktop: PropTypes.bool,
  isInputFocused: PropTypes.bool,
  isPrivateChat: PropTypes.bool,
  isPrivateUserChat: PropTypes.bool,
  messages: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  openDrawer: PropTypes.func,
  recipient: PropTypes.string,
  roomName: PropTypes.string,
  rosterUsers: PropTypes.objectOf(PropTypes.object),
  setIsInputFocused: PropTypes.func,
};

ChatMobileMessages.defaultProps = {
  hideInputInPrivateChat: false,
  isBroadcasterChat: false,
  isDesktop: false,
  isInputFocused: false,
  isPrivateChat: false,
  isPrivateUserChat: false,
  messages: [],
  openDrawer: () => undefined,
  recipient: undefined,
  roomName: undefined,
  rosterUsers: undefined,
  setIsInputFocused: undefined,
};

export default connect(appSelector, (dispatch) => {
  return { appActions: bindActionCreators(appActions, dispatch) };
})(ChatMobileMessages);
